import React, { useState, useCallback } from 'react';
import {
  objectOf, shape, func, bool, string
} from 'prop-types';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _orderBy from 'lodash/orderBy';
import _keyBy from 'lodash/keyBy';
import _uniq from 'lodash/uniq';
import { QueryProvider } from '@thd-nucleus/data-sources';
import { QuickView } from '@thd-olt-component-react/quick-view';
import {
  Tab, TabController, TabList, TabPanel, Alert
} from '@one-thd/sui-atomic-components';
import { Carousel } from '@one-thd/sui-carousel';
import classNames from 'classnames';
import { Choice, BundleChoice, AccessoryChoice } from '../choice';
import { withTemplateData } from '../hoc';
import {
  bundleDataShape, carouselBreakpointsShape, choiceShape, metadataShape, optionShape, productDataShape
} from '../types';
import { MobileChoicePlaceholders } from '../../placeholders/ChoicePlaceholders';
import {
  useCompatibilityStore
} from '../../store';
import { getEnhancedChoicesMobile } from '../utils';
import { CONFIGURATOR } from '../../constants';
import { optionIncludesNonViz } from './utils';

const handleTablistStyles = (isSelected) => {
  const baseClasses = classNames(
    'sui-tab-base',
    'sui-tab-base-overrides',
    'sui-text-lg',
    'sui-leading-tight',
    'sui-tracking-normal',
    'sui-normal-case',
    'sui-line-clamp',
    'focus-visible:sui-bg-focus focus-visible:sui-text-primary',
    'enabled:hover:sui-bg-subtle enabled:hover:sui-text-primary',
  );
  const selectedClass = 'sui-font-bold sui-text-primary';
  const classes = classNames(`${baseClasses}`, {
    [`${selectedClass}`]: isSelected,
  });
  return classes;
};
const Mobile = (props) => {
  const {
    options,
    choices,
    customCarouselBreakpoints,
    handleQuickView,
    metadata,
    productData,
    onSelect,
    setQuickView,
    quickView,
    zipCode,
    bundles
  } = props;
  const [value, setValue] = useState(CONFIGURATOR.OPTIONS.DEFAULT_BUILDER_OPTION_INDEX);
  const disabled = useCompatibilityStore((state) => state.disabled);
  const enhancedChoices = getEnhancedChoicesMobile({
    index: Number(value),
    options,
    metadata,
    productData,
    zipCode,
    disabled,
    choices,
    bundles
  });
  const sortedChoices = _orderBy((enhancedChoices), ['disabled', 'isAvailable'], ['asc', 'desc']);
  const handleChange = useCallback((event, newValue) => {
    setValue(newValue);
  }, []);

  return (
    <div className="sui-w-full">
      <TabController
        value={value}
      >
        <TabList
          allowScrollButtonsMobile
          variant="scrollable"
          aria-label="scrollable auto tabs"
          onChange={handleChange}
        >
          {_map(options, (option, index) => {
            const selected = value === index;
            return (
              <Tab
                className={handleTablistStyles(selected)}
                key={option.internalName}
                label={option.shortLabel}
                value={index}
                data-name={option.internalName}
                data-description={option.longLabel}
              />
            );
          })}
        </TabList>
        <TabPanel
          style={{ padding: '5px' }}
          value={value}
        >
          <div className="sui-block option" role="button" tabIndex={0}>
            {_isEmpty(choices) && (
              <MobileChoicePlaceholders />
            )}
            {!_isEmpty(choices) && (
              <div className="sui-block">
                {optionIncludesNonViz(options[value], CONFIGURATOR.OPTIONS.NON_VISUALIZED_OPTION)
                  && options[value]?.longLabel
                  && (
                    <div className="sui-text-center">
                      <Alert status="info">{options[value].longLabel}</Alert>
                    </div>
                  )}
                <Carousel
                  cssStyle
                  loop={false}
                  navigation={false}
                  SwiperProps={{
                    onSlidesLengthChange: (swiper) => swiper.slideTo(0),
                    simulateTouch: false,
                    allowTouchMove: true
                  }}
                  slidesPerView={3}
                  spaceBetween={7}
                  disableShadow
                  breakpoints={customCarouselBreakpoints}
                >
                  {_map(sortedChoices, (choice) => {
                    const { id = null, sku, internalName } = choice;
                    const {
                      BundleDesign: bundleDesign = null,
                      ProductType: productType = null
                    } = metadata?.[id]?.metadata;
                    if (bundleDesign) {
                      return (
                        <BundleChoice
                          key={internalName}
                          choice={choice}
                          quickViewHandler={handleQuickView}
                          choiceMetaData={metadata?.[id]?.metadata}
                          product={productData?.[sku]}
                          onSelect={onSelect}
                          zipCode={zipCode}
                          bundle={bundles?.[bundleDesign]}
                          metadata={
                            _keyBy(
                              _map(
                                _uniq(bundles?.[bundleDesign]?.bundleChoices),
                                (choiceId) => metadata?.[choiceId]),
                              'choiceId'
                            )
                          }
                        />
                      );
                    }
                    if (productType === CONFIGURATOR.CHOICE.ACCESSORY) {
                      return (
                        <div key={internalName}>
                          <AccessoryChoice
                            choice={choice}
                            quickViewHandler={handleQuickView}
                            choiceMetaData={metadata?.[id]?.metadata}
                            product={productData?.[sku]}
                            zipCode={zipCode}
                          />
                        </div>
                      );
                    }
                    return (
                      <Choice
                        key={internalName}
                        choice={choice}
                        quickViewHandler={handleQuickView}
                        choiceMetaData={metadata?.[id]?.metadata}
                        product={productData?.[sku]}
                        onSelect={onSelect}
                        zipCode={zipCode}
                      />
                    );
                  })}
                </Carousel>
              </div>
            )}
            {quickView?.itemId && (
              <QueryProvider cacheKey="quick-view-mobile">
                <QuickView
                  itemId={quickView?.itemId}
                  isOpen={quickView?.isOpen}
                  onClose={() => setQuickView({ itemId: null, isOpen: false })}
                />
              </QueryProvider>
            )}
          </div>
        </TabPanel>
      </TabController>
    </div>
  );
};
Mobile.propTypes = {
  options: objectOf(optionShape).isRequired,
  choices: objectOf(choiceShape),
  customCarouselBreakpoints: carouselBreakpointsShape.isRequired,
  handleQuickView: func.isRequired,
  metadata: metadataShape,
  onSelect: func.isRequired,
  productData: objectOf(productDataShape),
  quickView: shape({
    isOpen: bool,
    itemId: string,
  }).isRequired,
  setQuickView: func.isRequired,
  zipCode: string,
  bundles: objectOf(bundleDataShape)
};
Mobile.defaultProps = {
  choices: {},
  zipCode: '30339',
  metadata: {},
  productData: {},
  bundles: {}
};

export default withTemplateData(Mobile);
